/* eslint-disable max-lines*/

import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';

import { JobsWrapperActions } from './jobs-wrapper.actions';
import { MapperService } from '../../../services/mapper.service';
import { FetchOneJobsFullActionContext, OfferScope } from '../../../types';
import { JobsBasicActions, JobsBasicSelectors } from '../jobs-basic/jobs-basic.store';
import { JobsBriefActions } from '../jobs-brief/jobs-brief.store';
import { JobsFullActions } from '../jobs-full/jobs-full.store';
import { JobItemsActions, JobItemsSelectors } from '../jobs-item/jobs-item.store';

@Injectable({
  providedIn: 'root',
})
export class JobsWrapperEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  private readonly hasForceNewOffersEndpointFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.ForceNewOffersEndpointFlag),
  );

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.fetchCollection),
      switchMap(() => [JobsBasicActions.fetchCollection()]),
    ),
  );

  public fetchCollectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBasicActions.fetchCollectionSuccess),
      switchMap(() => [JobsWrapperActions.fetchCollectionSuccess()]),
    ),
  );

  public fetchCollectionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBasicActions.fetchCollectionFailed),
      switchMap(data => [JobsWrapperActions.fetchActiveCollectionFailed(data)]),
    ),
  );

  public fetchActiveCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.fetchActiveCollection),
      switchMap(() => [JobItemsActions.fetchActiveCollection()]),
    ),
  );

  public updateFromItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.fetchCollectionSuccess),
      switchMap(({ payload }) => [
        JobsBriefActions.upsertMany({
          payload: { data: payload.data, scope: <OfferScope>payload.scope },
        }),
      ]),
    ),
  );

  public fetchActiveCollectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.fetchActiveCollectionSuccess),
      switchMap(() => [JobsWrapperActions.fetchActiveCollectionSuccess()]),
    ),
  );

  public fetchActiveCollectionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.fetchActiveCollectionFailed),
      switchMap(data => [JobsWrapperActions.fetchActiveCollectionFailed(data)]),
    ),
  );

  public fetchArchivedCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.fetchArchivedCollection),
      switchMap(() => [JobsBriefActions.fetchArchivedCollection()]),
    ),
  );

  public fetchArchivedCollectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBriefActions.fetchArchivedCollectionSuccess),
      switchMap(() => [JobsWrapperActions.fetchArchivedCollectionSuccess()]),
    ),
  );

  public fetchArchivedCollectionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBriefActions.fetchArchivedCollectionFailed),
      switchMap(data => [JobsWrapperActions.fetchArchivedCollectionFailed(data)]),
    ),
  );

  public fetchFullOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.fetchFullOne),
      switchMap(data => [
        JobsFullActions.fetchOne({
          payload: {
            ...data.payload,
            context: FetchOneJobsFullActionContext.FetchFullOne,
          },
        }),
      ]),
    ),
  );

  public jobUpdatedFromWebsocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.jobUpdatedFromWebsocket),
      switchMap(data => [
        JobsFullActions.fetchOne({
          payload: {
            ...data.payload,
            context: FetchOneJobsFullActionContext.JobUpdatedFromWebsocket,
          },
        }),
      ]),
    ),
  );

  public jobCreatedFromWebsocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.jobCreatedFromWebsocket),
      switchMap(data => [
        JobsFullActions.fetchOne({
          payload: {
            ...data.payload,
            context: FetchOneJobsFullActionContext.JobCreatedFromWebsocket,
          },
        }),
      ]),
    ),
  );

  public updateFromFull$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneSuccess),
      switchMap(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return [
            JobsBasicActions.upsertOne({
              payload: { data: MapperService.mapJobFullToJobBasic(payload.job) },
            }),
          ];

        return [
          JobItemsActions.upsertOne({
            payload: { data: payload.job },
          }),
          JobsBriefActions.upsertOne({
            payload: { data: payload.job },
          }),
        ];
      }),
    ),
  );

  public fetchFullOneSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneSuccess),
      filter(data => data.payload.context === FetchOneJobsFullActionContext.FetchFullOne),
      switchMap(data => [JobsWrapperActions.fetchFullOneSuccess(data)]),
    ),
  );

  public fetchFullOneFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneFailed),
      filter(data => data.payload.context === FetchOneJobsFullActionContext.FetchFullOne),
      switchMap(data => [JobsWrapperActions.fetchFullOneFailed(data)]),
    ),
  );

  public jobUpdatedFromWebsocketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneSuccess),
      filter(
        data => data.payload.context === FetchOneJobsFullActionContext.JobUpdatedFromWebsocket,
      ),
      switchMap(data => [JobsWrapperActions.jobUpdatedFromWebsocketSuccess(data)]),
    ),
  );

  public jobUpdatedFromWebsocketFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneFailed),
      filter(
        data => data.payload.context === FetchOneJobsFullActionContext.JobUpdatedFromWebsocket,
      ),
      switchMap(data => [JobsWrapperActions.jobUpdatedFromWebsocketFailed(data)]),
    ),
  );

  public jobCreatedFromWebsocketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneSuccess),
      filter(
        data => data.payload.context === FetchOneJobsFullActionContext.JobCreatedFromWebsocket,
      ),
      switchMap(data => [JobsWrapperActions.jobCreatedFromWebsocketSuccess(data)]),
    ),
  );

  public jobCreatedFromWebsocketFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.fetchOneFailed),
      filter(
        data => data.payload.context === FetchOneJobsFullActionContext.JobCreatedFromWebsocket,
      ),
      switchMap(data => [JobsWrapperActions.jobCreatedFromWebsocketFailed(data)]),
    ),
  );

  public updateFull$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.updateFull),
      switchMap(({ payload }) => of(JobsFullActions.update({ payload }))),
    ),
  );

  public updateFullSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSuccess),
      switchMap(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return [
            JobsBasicActions.upsertOne({
              payload: { data: MapperService.mapJobFullToJobBasic(payload.data) },
            }),
            JobsWrapperActions.updateFullSuccess({ payload: { data: payload.data } }),
          ];

        return [
          JobItemsActions.upsertOne({
            payload: { data: payload.data },
          }),
          JobsBriefActions.upsertOne({
            payload: { data: payload.data },
          }),
          JobsWrapperActions.updateFullSuccess({ payload: { data: payload.data } }),
        ];
      }),
    ),
  );

  public updateFullFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateFailed),
      switchMap(({ payload }) => [JobsWrapperActions.updateFullFailed({ payload })]),
    ),
  );

  public follow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.follow),
      switchMap(({ payload }) => of(JobsFullActions.follow({ payload }))),
    ),
  );

  public followSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.followSuccess),
      switchMap(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return [
            JobsBasicActions.upsertOne({
              payload: { data: MapperService.mapJobFullToJobBasic(payload.job) },
            }),
            JobsWrapperActions.followSuccess({ payload: { followed: payload.job.followed } }),
          ];

        return [
          JobItemsActions.upsertOne({
            payload: { data: payload.job },
          }),
          JobsBriefActions.upsertOne({
            payload: { data: payload.job },
          }),
          JobsWrapperActions.followSuccess({ payload: { followed: payload.job.followed } }),
        ];
      }),
    ),
  );

  public followFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.followFailed),
      switchMap(({ payload }) => [JobsWrapperActions.followFailed({ payload })]),
    ),
  );

  public unfollow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.unfollow),
      switchMap(({ payload }) => of(JobsFullActions.unfollow({ payload }))),
    ),
  );

  public unfollowSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.unfollowSuccess),
      switchMap(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return [
            JobsBasicActions.upsertOne({
              payload: { data: MapperService.mapJobFullToJobBasic(payload.job) },
            }),
            JobsWrapperActions.unfollowSuccess({ payload: { followed: payload.job.followed } }),
          ];

        return [
          JobItemsActions.upsertOne({
            payload: { data: payload.job },
          }),
          JobsBriefActions.upsertOne({
            payload: { data: payload.job },
          }),
          JobsWrapperActions.unfollowSuccess({ payload: { followed: payload.job.followed } }),
        ];
      }),
    ),
  );

  public unfollowFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.unfollowFailed),
      switchMap(({ payload }) => [JobsWrapperActions.unfollowFailed({ payload })]),
    ),
  );

  public duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.duplicate),
      switchMap(({ payload }) => of(JobsFullActions.duplicate({ payload }))),
    ),
  );

  public duplicateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.duplicateSuccess),
      switchMap(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return [
            JobsWrapperActions.duplicateSuccess({ payload: { offer: payload.offer } }),
            JobsBasicActions.fetchCollection(),
          ];

        return [
          JobsWrapperActions.duplicateSuccess({ payload: { offer: payload.offer } }),
          JobItemsActions.fetchActiveCollection(),
        ];
      }),
    ),
  );

  public duplicateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.duplicateFailed),
      switchMap(({ payload }) => [JobsWrapperActions.duplicateFailed({ payload })]),
    ),
  );

  public create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.create),
      switchMap(({ payload }) => of(JobsFullActions.create({ payload }))),
    ),
  );

  public createSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.createSuccess({ payload })]),
    ),
  );

  public createFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFailed),
      switchMap(({ payload }) => [JobsWrapperActions.createFailed({ payload })]),
    ),
  );

  public createFromRequisition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.createFromRequisition),
      switchMap(({ payload }) => of(JobsFullActions.createFromRequisition({ payload }))),
    ),
  );

  public createFromRequisitionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromRequisitionSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.createFromRequisitionSuccess({ payload })]),
    ),
  );

  public createFromRequisitionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromRequisitionFailed),
      switchMap(({ payload }) => [JobsWrapperActions.createFromRequisitionFailed({ payload })]),
    ),
  );

  public createFromTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.createFromTemplate),
      switchMap(({ payload }) => of(JobsFullActions.createFromTemplate({ payload }))),
    ),
  );

  public createFromTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromTemplateSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.createFromTemplateSuccess({ payload })]),
    ),
  );

  public createFromTemplateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromTemplateFailed),
      switchMap(({ payload }) => [JobsWrapperActions.createFromTemplateFailed({ payload })]),
    ),
  );

  public createFromPosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.createFromPosition),
      switchMap(({ payload }) => of(JobsFullActions.createFromPosition({ payload }))),
    ),
  );

  public createFromPositionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromPositionSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.createFromPositionSuccess({ payload })]),
    ),
  );

  public createFromPositionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.createFromPositionFailed),
      switchMap(({ payload }) => [JobsWrapperActions.createFromPositionFailed({ payload })]),
    ),
  );

  public changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.changeStatus),
      concatLatestFrom(({ payload }) => {
        if (this.hasForceNewOffersEndpointFlag())
          return this.store.select(JobsBasicSelectors.byId(payload.id));
        return this.store.select(JobItemsSelectors.byId(payload.id));
      }),
      switchMap(([{ payload }, jobItem]) =>
        of(
          JobsFullActions.changeStatus({
            payload: { ...payload, currentStatus: jobItem?.status ?? payload.currentStatus! },
          }),
        ),
      ),
    ),
  );

  public changeStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.changeStatusSuccess),
      switchMap(({ payload }) => [
        ...(payload.isChangingScope && this.hasForceNewOffersEndpointFlag()
          ? [
              JobItemsActions.removeOne({ payload: { data: payload.offer.id } }),
              JobsBriefActions.removeOne({ payload: { data: payload.offer.id } }),
            ]
          : []),
        ...(this.hasForceNewOffersEndpointFlag()
          ? [
              JobsBasicActions.upsertOne({
                payload: { data: MapperService.mapJobFullToJobBasic(payload.offer) },
              }),
            ]
          : [
              JobItemsActions.upsertOne({
                payload: { data: payload.offer },
              }),
              JobsBriefActions.upsertOne({
                payload: { data: payload.offer },
              }),
            ]),
        JobsWrapperActions.changeStatusSuccess({ payload: { offer: payload.offer } }),
      ]),
    ),
  );

  public changeStatusFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.changeStatusFailed),
      switchMap(({ payload }) => [JobsWrapperActions.changeStatusFailed({ payload })]),
    ),
  );

  public preview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.preview),
      switchMap(({ payload }) => [JobsFullActions.preview({ payload })]),
    ),
  );

  public previewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.previewSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.previewSuccess({ payload })]),
    ),
  );

  public previewFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.previewFailed),
      switchMap(({ payload }) => [JobsWrapperActions.previewFailed({ payload })]),
    ),
  );

  public uploadSocialMediaImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.uploadSocialMediaImage),
      switchMap(({ payload }) => [JobsFullActions.uploadSocialMediaImage({ payload })]),
    ),
  );

  public uploadSocialMediaImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.uploadSocialMediaImageSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.uploadSocialMediaImageSuccess({ payload })]),
    ),
  );

  public uploadSocialMediaImageFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.uploadSocialMediaImageFailed),
      switchMap(({ payload }) => [JobsWrapperActions.uploadSocialMediaImageFailed({ payload })]),
    ),
  );

  public uploadCoverImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.uploadCoverImage),
      switchMap(({ payload }) => [JobsFullActions.uploadCoverImage({ payload })]),
    ),
  );

  public uploadCoverImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.uploadCoverImageSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.uploadCoverImageSuccess({ payload })]),
    ),
  );

  public uploadCoverImageFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.uploadCoverImageFailed),
      switchMap(({ payload }) => [JobsWrapperActions.uploadCoverImageFailed({ payload })]),
    ),
  );

  public updateSlug$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.updateSlug),
      switchMap(({ payload }) => [JobsFullActions.updateSlug({ payload })]),
    ),
  );

  public updateSlugSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSlugSuccess),
      switchMap(({ payload }) => [JobsWrapperActions.updateSlugSuccess({ payload })]),
    ),
  );

  public updateSlugFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.updateSlugFailed),
      switchMap(({ payload }) => [JobsWrapperActions.updateSlugFailed({ payload })]),
    ),
  );

  public insertDataFromTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.insertDataFromTemplate),
      switchMap(({ payload }) => of(JobsFullActions.insertDataFromTemplate({ payload }))),
    ),
  );

  public insertDataFromTemplateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.insertDataFromTemplateSuccess),
      switchMap(({ payload }) => [
        ...(this.hasForceNewOffersEndpointFlag()
          ? [
              JobsBasicActions.upsertOne({
                payload: { data: MapperService.mapJobFullToJobBasic(payload.data.offer) },
              }),
            ]
          : [
              JobItemsActions.upsertOne({
                payload: { data: payload.data.offer },
              }),
              JobsBriefActions.upsertOne({
                payload: { data: payload.data.offer },
              }),
            ]),
        JobsWrapperActions.insertDataFromTemplateSuccess({ payload }),
      ]),
    ),
  );

  public insertDataFromTemplateFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsFullActions.insertDataFromTemplateFailed),
      switchMap(({ payload }) => [JobsWrapperActions.insertDataFromTemplateFailed({ payload })]),
    ),
  );

  public updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.updatePositions),
      switchMap(({ payload }) => [JobItemsActions.updatePositions({ payload })]),
    ),
  );

  public updatePositionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.updatePositionSuccess),
      switchMap(() => [JobsWrapperActions.updatePositionsSuccess()]),
    ),
  );

  public updatePositionsFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.updatePositionsFailed),
      switchMap(({ payload }) => [JobsWrapperActions.updatePositionsFailed({ payload })]),
    ),
  );

  public switchJobsToProsemirror$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.switchJobsToProsemirror),
      switchMap(() => [
        this.hasForceNewOffersEndpointFlag()
          ? JobsBasicActions.switchJobsToProsemirror()
          : JobItemsActions.switchJobsToProsemirror(),
      ]),
    ),
  );

  public switchJobsToProsemirrorSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        JobsBasicActions.switchJobsToProsemirrorSuccess,
        JobItemsActions.switchJobsToProsemirrorSuccess,
      ),
      switchMap(() => [JobsWrapperActions.switchJobsToProsemirrorSuccess()]),
    ),
  );

  public switchJobsToProsemirrorFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        JobsBasicActions.switchJobsToProsemirrorFailed,
        JobItemsActions.switchJobsToProsemirrorFailed,
      ),
      switchMap(payload => [JobsWrapperActions.switchJobsToProsemirrorFailed(payload)]),
    ),
  );

  public jobDeletedFromWebsocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.jobDeletedFromWebsocket),
      switchMap(({ payload }) => [
        ...(this.hasForceNewOffersEndpointFlag()
          ? [JobsBasicActions.removeOne({ payload: { data: payload.id } })]
          : [
              JobItemsActions.removeOne({ payload: { data: payload.id } }),
              JobsBriefActions.removeOne({ payload: { data: payload.id } }),
            ]),
        JobsFullActions.removeOne({ payload: { data: payload.id } }),
        JobsWrapperActions.jobDeletedFromWebsocketSuccess({ payload: { id: payload.id } }),
      ]),
    ),
  );

  public jobsUpdatedFromWebsocket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.jobsUpdatedFromWebsocket),
      switchMap(() => [
        ...(this.hasForceNewOffersEndpointFlag()
          ? [JobsWrapperActions.fetchCollection()]
          : [
              JobsWrapperActions.fetchActiveCollection(),
              JobsWrapperActions.fetchArchivedCollection(),
            ]),
      ]),
    ),
  );

  public remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsWrapperActions.removeOne),
      switchMap(({ payload }) => [
        JobItemsActions.removeOnePessimistic({ payload: { data: payload.id } }),
        ...(this.hasForceNewOffersEndpointFlag()
          ? [JobsBasicActions.removeOnePessimistic({ payload: { data: payload.id } })]
          : [JobItemsActions.removeOnePessimistic({ payload: { data: payload.id } })]),
      ]),
    ),
  );

  public removeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        JobItemsActions.removeOnePessimisticSuccess,
        JobsBasicActions.removeOnePessimisticSuccess,
      ),
      switchMap(({ payload }) => [
        ...(this.hasForceNewOffersEndpointFlag()
          ? [JobsBasicActions.removeOnePessimistic({ payload: { data: payload.data.id } })]
          : [JobsBasicActions.removeOnePessimistic({ payload: { data: payload.data.id } })]),
        JobsFullActions.removeOne({ payload: { data: payload.data.id } }),
        JobsWrapperActions.removeOneSuccess({ payload: { id: payload.data.id } }),
      ]),
    ),
  );

  public removeFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobItemsActions.removeOnePessimisticFailed),
      switchMap(({ payload }) => [
        JobsWrapperActions.removeOneFailed({
          payload: { id: <number>payload.data, error: payload.error },
        }),
      ]),
    ),
  );
}
